import { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FileList } from "@/components/shared/upload/FileList";
import { UploadForm } from "@/components/shared/upload/UploadForm";

import { RootState, useAppDispatch, useAppSelector } from "@/lib/redux/root";
import { useUpload, fileStore } from "@/lib/hooks/useUpload";
import { clearFiles } from "@/lib/redux/slices/upload.slice";
import { resetDueDate } from "@/lib/redux/slices/uploadform.slice";

export default function UploadPage() {
	const navigate = useNavigate();
	const params = useParams<{ slug: string; product: string }>();
	const [searchParams] = useSearchParams();
	const slug = params.slug as string;
	const product = params.product as string;
	const { files, inProgess } = useAppSelector(
		(state: RootState) => state.upload
	);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const dropZoneRef = useRef<HTMLDivElement>(null);

	const dispatch = useAppDispatch();
	const { onSelectFiles, onDropFiles } = useUpload();

	useEffect(() => {
		dispatch(resetDueDate());
	}, []);

	const { isCompleted, orderId } = useAppSelector(
		(state: RootState) => state.order
	);

	const handleAddMore = useCallback(() => {
		fileInputRef.current?.click();
	}, []);
	// 🔹 Mobile bottom sheet state
	const [isSheetOpen, setIsSheetOpen] = useState(false);

	// lock body scroll when sheet is open (mobile UX)
	useEffect(() => {
		if (isSheetOpen) {
			const prev = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			return () => {
				document.body.style.overflow = prev || "";
			};
		}
	}, [isSheetOpen]);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		onSelectFiles(e);
		// clear the input so selecting the same file again will fire change
		try {
			if (e.currentTarget) e.currentTarget.value = "";
			if (fileInputRef.current) fileInputRef.current.value = "";
		} catch {
			/* ignore (defensive) */
		}
	};

	// -------- Folder Drag & Drop Logic --------
	const handleDrop = useCallback(
		async (event: DragEvent) => {
			event.preventDefault();
			event.stopPropagation();
			onDropFiles(event as any);
		},
		[onDropFiles]
	);

	const handleDragOver = useCallback((e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	useEffect(() => {
		const dropZone = dropZoneRef.current;
		if (!dropZone || inProgess) {
			return;
		}

		dropZone.addEventListener("drop", handleDrop);
		dropZone.addEventListener("dragover", handleDragOver);

		return () => {
			dropZone.removeEventListener("drop", handleDrop);
			dropZone.removeEventListener("dragover", handleDragOver);
		};
	}, [handleDrop, handleDragOver, inProgess]);

	const [hasInitialized, setHasInitialized] = useState(false);
	const [filesLost, setFilesLost] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	// Check if file objects are still in memory (they can be lost on page reload)
	useEffect(() => {
		// Mark that we've done the initial check
		setIsInitialLoad(false);

		if (files.length > 0) {
			// Check if at least one file still exists in the fileStore
			const hasValidFiles = files.some(
				(f) => fileStore.getFileById(f.id) !== null
			);
			if (!hasValidFiles) {
				// Files metadata exists in Redux but actual File objects are lost
				setFilesLost(true);
				dispatch(clearFiles()); // Clear stale metadata
			} else {
				setHasInitialized(true);
			}
		} else {
			// No files in Redux - this is normal for initial page load
			setHasInitialized(true);
		}
	}, [files, dispatch]);

	useEffect(() => {
		if (hasInitialized && files.length === 0 && !filesLost && !isInitialLoad) {
			navigate(`/${product}/${slug}`, { replace: true });
		}
	}, [
		files,
		hasInitialized,
		filesLost,
		navigate,
		isInitialLoad,
		product,
		slug,
	]);

	useEffect(() => {
		if (isCompleted) {
			navigate(`/${product}/${slug}/order/success?orderId=${orderId}`);
		}
	}, [isCompleted, orderId, product, navigate, slug]);

	// Build URL with preserved query params
	const getUrlWithParams = (path: string) => {
		const queryString = searchParams.toString();
		return queryString ? `${path}?${queryString}` : path;
	};

	// Show recovery UI ONLY if files were actually lost (not on initial load)
	if (filesLost) {
		return (
			<main className='flex flex-1 items-center justify-center px-6 py-12'>
				<div className='text-center'>
					<h2 className='mb-4 text-xl font-semibold text-gray-800'>
						Session expired or page was refreshed
					</h2>
					<p className='mb-6 text-gray-600'>
						Please go back and select your files again.
					</p>
					<button
						onClick={() => navigate(getUrlWithParams(`/${product}/${slug}`))}
						className='rounded-lg bg-red-600 px-6 py-3 text-white transition-colors hover:bg-red-700'
					>
						Go Back to Upload
					</button>
				</div>
			</main>
		);
	}

	// Show loading state during initial check
	if (isInitialLoad) {
		return (
			<main className='flex flex-1 items-center justify-center px-6 py-12'>
				<div className='text-center'>
					<p className='text-gray-600'>Loading...</p>
				</div>
			</main>
		);
	}

	// Show desktop layout (lg+) and mobile layout (lg:hidden) with bottom sheet
	return (
		<main
			ref={dropZoneRef}
			className='flex-1 border-gray-300 transition-colors lg:px-6 lg:py-12'
		>
			<input
				ref={fileInputRef}
				type='file'
				multiple
				className='hidden'
				onChange={handleFileSelect}
			/>

			{/* ===== Desktop ===== */}
			<div className='mx-auto hidden max-w-6xl gap-6 lg:flex'>
				<div className='w-1/3'>
					<FileList onAddMore={handleAddMore} />
				</div>
				<div className='w-2/3'>
					<UploadForm />
				</div>
			</div>

			{/* ===== Mobile Form ===== */}
			<div className='mx-auto h-screen max-w-2xl lg:hidden'>
				<div className='h-full overflow-auto pb-32'>
					<UploadForm />
				</div>
			</div>

			{/* ===== Mobile Bottom Summary Bar ===== */}
			<div className='fixed inset-x-4 bottom-4 z-40 lg:hidden'>
				<button
					onClick={() => setIsSheetOpen(true)}
					className='mx-auto flex w-full items-center justify-between gap-3 rounded-full bg-white/98 px-4 py-3 shadow-md'
					aria-expanded={isSheetOpen}
					aria-controls='mobile-filelist-sheet'
				>
					<div className='flex items-center gap-3'>
						<span className='text-sm font-medium'>{files.length} Files</span>
					</div>
					<div className='flex items-center gap-3'>
						<svg
							className={`h-4 w-4 transform transition-transform ${isSheetOpen ? "rotate-180" : ""}`}
							viewBox='0 0 24 24'
							fill='none'
							aria-hidden
						>
							<path
								d='M6 9l6 6 6-6'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</div>
				</button>
			</div>

			{/* Backdrop */}
			<div
				className={`fixed inset-0 z-45 transition-opacity lg:hidden ${isSheetOpen ? "pointer-events-auto opacity-50" : "pointer-events-none opacity-0"}`}
				style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
				onClick={() => setIsSheetOpen(false)}
				aria-hidden={!isSheetOpen}
			/>

			{/* ===== Mobile Bottom Sheet ===== */}
			<aside
				id='mobile-filelist-sheet'
				className={`fixed inset-x-0 bottom-0 z-50 transform-gpu transition-transform duration-300 lg:hidden ${isSheetOpen ? "translate-y-0" : "translate-y-full"}`}
				role='dialog'
				aria-modal='true'
			>
				{/* Minimal sheet: only FileList (no header/handle) */}
				<div className='mx-0 mb-0 bg-white'>
					<div className='max-h-[85vh] overflow-y-auto'>
						<FileList onAddMore={handleAddMore} />
					</div>
				</div>
			</aside>
		</main>
	);
}
